// Iter · Feb 2026 — Full-featured Content Calendar Timeline.
//
// Adds on top of the previous inline TimelineView:
//   • Freeze first column (Brand) while horizontally scrolling
//   • Sticky day-number header row per month
//   • Fullscreen toggle (Escape exits)
//   • Gridlines on/off toggle
//   • Brand → Package → Category (3-level) expansion
//   • Multi-month compare view (2/3/6 stacked, one below another)
//   • Insight chip filter — accepts an `activeFilter` from
//     `TimelineInsights` and shows a "clear" pill when applied
//   • "Copy dates from previous month" dialog (backend endpoint
//     POST /workspaces/{ws}/content-calendar/carry-forward-dates)
//
// Data model: this component fetches its own months from
// `/workspaces/{ws}/content-items-enriched?month=YYYY-MM` and reloads
// on drag-drop or after a carry-forward apply.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { CalendarHoverCard } from "@/pages/app/ContentCalendar";
import TimelineInsights from "@/pages/app/TimelineInsights";
import PackageHoverPreview from "@/components/packages/PackageHoverPreview";
import {
  Maximize2, Minimize2, Grid3x3, Grid2x2, Copy, X, RefreshCw, Loader2, Shuffle,
} from "lucide-react";

function monthStr(y, m) { return `${y}-${String(m).padStart(2, "0")}`; }
function shiftMonth(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return monthStr(d.getFullYear(), d.getMonth() + 1);
}
function monthLabel(ym) {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en", { month: "long", year: "numeric" });
}
function daysInMonth(ym) {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m, 0).getDate();
}

// Colour picker copied from prior TimelineView. Keeps parity with
// legacy behaviour so screenshots and muscle memory don't break.
function dotColour(it) {
  return it.activity_type_color
    || (it.status === "published" ? "#059669"
        : it.status === "cancelled" ? "#ef4444"
        : "#8b5cf6");
}

export default function ContentCalendarTimeline({ wsId, initialMonth, onOpen, onOpenTask, members = [], workspaceName, brandF = [], statusF = [], catF = [], projF = [], projects = [] }) {
  // Iter · Feb 2026 — Compare mode now takes N months BEFORE and AFTER
  // the current month so users can see history + forecast side by side.
  // Past/Future are independent so you can look 3 back + 1 ahead etc.
  const [pastMonths, setPastMonths] = useState(0);
  const [futureMonths, setFutureMonths] = useState(0);
  const [monthsData, setMonthsData] = useState({}); // { "YYYY-MM": items[] }
  const [loading, setLoading] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [gridlines, setGridlines] = useState(true);
  const [showCategoryLabels, setShowCategoryLabels] = useState(false);
  // R268~8 · Feb 2026 — Optional numbering ("1/4", "2/4", …) on every
  // deliverable chip. Numbering is computed per row scope (brand /
  // package / category) so each chip carries an index that makes
  // sense to the row it belongs to. Chips morph from a dot into a
  // small filled pill with light text on the deliverable's dark
  // dot-colour background for readability.
  const [showNumbering, setShowNumbering] = useState(false);
  // Iter · Feb 2026 · R257 — Group deliverables under Package by
  //  "category" (default) OR by "title" (base_name). Lets PMs pivot the
  //  timeline when they care about the line item cadence instead of
  //  the category rollup.
  const [groupMode, setGroupMode] = useState("category"); // "category" | "title"
  // Multi-level expansion state — track expanded brand/package/category keys.
  const [expBrands, setExpBrands] = useState(() => new Set());
  const [expPkgs, setExpPkgs] = useState(() => new Set());
  const [expCats, setExpCats] = useState(() => new Set());
  const [dragging, setDragging] = useState(null); // { id, sourceMonth }
  const [dragOver, setDragOver] = useState(null); // { month, day }
  const [activeFilter, setActiveFilter] = useState(null); // { kind, itemIds, label }
  const [carryOpen, setCarryOpen] = useState(false);
  const [respreadOpen, setRespreadOpen] = useState(false);
  // Iter · Feb 2026 — Manual column width override so users can drag
  // the timeline wider when they need more room for busy days.
  const [colWidthOverride, setColWidthOverride] = useState(null); // px | null
  // Iter · Feb 2026 — Important days (global catalog + workspace custom
  // days) fetched once per visible year and reused across all month
  // grids so hover-cards can show the day's name/category/brands.
  const [importantDays, setImportantDays] = useState([]);
  useEffect(() => {
    if (!wsId) return;
    const year = Number(initialMonth.split("-")[0]);
    Promise.all([
      api.get(`/reference/special-days?year=${year}`).then((r) => r.data?.days || []).catch(() => []),
      api.get(`/workspaces/${wsId}/custom-days?year=${year}`).then((r) => r.data || []).catch(() => []),
    ]).then(([global, custom]) => {
      // Normalise shape: {date, name, category, emoji, description}
      const normed = [
        ...global.map((d) => ({ date: d.date, name: d.name, category: d.category, emoji: d.emoji, description: d.description })),
        ...custom.map((d) => ({ date: d.date, name: d.name, category: d.category, emoji: d.emoji, description: d.description })),
      ];
      setImportantDays(normed);
    });
  }, [wsId, initialMonth]);

  const monthList = useMemo(() => {
    const arr = [];
    for (let i = -pastMonths; i <= futureMonths; i++) arr.push(shiftMonth(initialMonth, i));
    return arr;
  }, [initialMonth, pastMonths, futureMonths]);
  const isCompareView = pastMonths + futureMonths > 0;

  const loadMonth = useCallback(async (m) => {
    if (!wsId) return;
    try {
      const r = await api.get(`/workspaces/${wsId}/content-items-enriched?month=${m}&limit=5000`);
      const rows = r.data?.rows || r.data || [];
      setMonthsData((prev) => ({ ...prev, [m]: rows }));
    } catch (e) {
      toast.error(apiErrorMessage(e, `Load ${m} failed`));
    }
  }, [wsId]);

  const loadAll = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      await Promise.all(monthList.map(loadMonth));
    } finally {
      setLoading(false);
    }
  }, [wsId, monthList, loadMonth]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Escape exits fullscreen.
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  const toggleSet = (setter) => (k) => setter((prev) => {
    const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n;
  });
  const toggleBrand = toggleSet(setExpBrands);
  const togglePkg = toggleSet(setExpPkgs);
  const toggleCat = toggleSet(setExpCats);

  const reschedule = async (itemId, targetMonth, dayNum) => {
    if (!itemId || !dayNum || !targetMonth) return;
    const [y, m] = targetMonth.split("-").map(Number);
    const newDate = `${y}-${String(m).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
    try {
      await api.patch(`/content-items/${itemId}`, { planned_date: newDate });
      toast.success(`Rescheduled to ${newDate}`);
      // Reload both source and target months.
      await Promise.all(monthList.map(loadMonth));
    } catch (e) {
      toast.error(apiErrorMessage(e, "Reschedule failed"));
    }
  };

  // Apply insight filter — only keep items whose id is in the filter set.
  // Feb 2026 · R258 — Also respect the page-level brand/status/category
  // filters so switching to Timeline no longer shows unfiltered data.
  const applyFilter = (items) => {
    let list = items;
    if (activeFilter?.itemIds?.length) {
      const s = new Set(activeFilter.itemIds);
      list = list.filter((it) => s.has(it.id));
    }
    if (brandF.length > 0) list = list.filter((it) => brandF.includes(it.brand_id));
    if (statusF.length > 0) list = list.filter((it) => statusF.includes(it.status));
    if (catF.length > 0) list = list.filter((it) => catF.includes(it.category));
    if (projF.length > 0) {
      list = list.filter((it) => {
        if (it.project_id && projF.includes(it.project_id)) return true;
        const names = new Set(it.project_names || []);
        const selNames = projects.filter((p) => projF.includes(p.id)).map((p) => p.name);
        return selNames.some((n) => names.has(n));
      });
    }
    return list;
  };

  return (
    <div className={fullscreen
        ? "fixed inset-0 z-[200] bg-background overflow-auto p-4 flex flex-col gap-2"
        : "flex flex-col gap-2"}
        data-testid="cc-timeline-view">
      {/* Toolbar — always visible */}
      <div className="flex items-center gap-2 flex-wrap px-2 py-1.5 border border-border bg-muted/20">
        <div className="flex items-center gap-1">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Compare</span>
          <Select value={String(pastMonths)} onValueChange={(v) => setPastMonths(Number(v))}>
            <SelectTrigger className="h-7 w-[92px] rounded-none text-[11px] px-2" data-testid="cc-tl-compare-past"
                           title="How many months BEFORE the current month to include">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">0 past</SelectItem>
              <SelectItem value="1">1 past</SelectItem>
              <SelectItem value="2">2 past</SelectItem>
              <SelectItem value="3">3 past</SelectItem>
              <SelectItem value="6">6 past</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-[10px] font-mono text-muted-foreground px-0.5">◀ ▶</span>
          <Select value={String(futureMonths)} onValueChange={(v) => setFutureMonths(Number(v))}>
            <SelectTrigger className="h-7 w-[108px] rounded-none text-[11px] px-2" data-testid="cc-tl-compare-future"
                           title="How many months AFTER the current month to include">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">0 future</SelectItem>
              <SelectItem value="1">1 future</SelectItem>
              <SelectItem value="2">2 future</SelectItem>
              <SelectItem value="3">3 future</SelectItem>
              <SelectItem value="6">6 future</SelectItem>
            </SelectContent>
          </Select>
          {isCompareView && (
            <span className="text-[10px] text-muted-foreground font-mono ml-1"
                  data-testid="cc-tl-compare-total">
              {monthList.length} mo
            </span>
          )}
        </div>
        <label className="flex items-center gap-1.5 text-[11px] cursor-pointer">
          <input type="checkbox" checked={showCategoryLabels}
                 onChange={(e) => setShowCategoryLabels(e.target.checked)}
                 data-testid="cc-tl-toggle-labels" />
          Category labels
        </label>
        <label className="flex items-center gap-1.5 text-[11px] cursor-pointer">
          <input type="checkbox" checked={gridlines}
                 onChange={(e) => setGridlines(e.target.checked)}
                 data-testid="cc-tl-toggle-gridlines" />
          Gridlines
        </label>
        <label className="flex items-center gap-1.5 text-[11px] cursor-pointer"
               title="Number each deliverable chip (per row) so you can reference them as Content #1/4, 2/4, …">
          <input type="checkbox" checked={showNumbering}
                 onChange={(e) => setShowNumbering(e.target.checked)}
                 data-testid="cc-tl-toggle-numbering" />
          Numbering
        </label>
        <div className="flex items-center gap-1">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Group by</span>
          <Select value={groupMode} onValueChange={setGroupMode}>
            <SelectTrigger className="h-7 w-[130px] rounded-none text-[11px] px-2"
                           data-testid="cc-tl-groupmode"
                           title="Group deliverables under each package by category or by deliverable title">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="category">Category</SelectItem>
              <SelectItem value="title">Deliverable title</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="ghost" size="sm" className="h-7 rounded-none text-[11px]"
                onClick={() => setCarryOpen(true)}
                data-testid="cc-tl-carry-btn">
          <Copy className="w-3.5 h-3.5 mr-1" /> Copy dates from previous month
        </Button>
        <Button variant="ghost" size="sm" className="h-7 rounded-none text-[11px]"
                onClick={() => setRespreadOpen(true)}
                title="Re-distribute every deliverable in this month evenly across the calendar. Great for fixing legacy data that piled items on one day."
                data-testid="cc-tl-respread-btn">
          <Shuffle className="w-3.5 h-3.5 mr-1" /> Re-spread dates
        </Button>
        <Button variant="ghost" size="sm" className="h-7 rounded-none text-[11px]"
                onClick={loadAll}
                disabled={loading}
                data-testid="cc-tl-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button variant="ghost" size="sm" className="h-7 rounded-none text-[11px]"
                onClick={() => setFullscreen((v) => !v)}
                data-testid="cc-tl-fullscreen">
          {fullscreen
            ? <><Minimize2 className="w-3.5 h-3.5 mr-1" /> Exit fullscreen</>
            : <><Maximize2 className="w-3.5 h-3.5 mr-1" /> Fullscreen</>}
        </Button>
        {/* Iter · Feb 2026 — Manual column-width slider so busy days get
             more room without waiting for the auto sizer. */}
        <div className="flex items-center gap-1" title="Drag to resize day columns">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Col</span>
          <input
            type="range"
            min={24} max={160} step={4}
            value={colWidthOverride ?? 32}
            onChange={(e) => setColWidthOverride(Number(e.target.value))}
            className="w-24 h-1 accent-primary cursor-ew-resize"
            data-testid="cc-tl-col-slider"
          />
          <span className="text-[10px] font-mono text-muted-foreground w-8">
            {colWidthOverride ?? "auto"}
          </span>
          {colWidthOverride != null && (
            <button className="text-[10px] text-muted-foreground hover:text-primary underline decoration-dotted underline-offset-2"
                    onClick={() => setColWidthOverride(null)}
                    data-testid="cc-tl-col-reset">
              reset
            </button>
          )}
        </div>
        <span className="ml-auto text-[10px] text-muted-foreground">
          {gridlines ? <Grid3x3 className="w-3 h-3 inline mr-1" /> : <Grid2x2 className="w-3 h-3 inline mr-1" />}
          Drag any dot to reschedule
        </span>
      </div>

      {/* Active filter pill */}
      {activeFilter && (
        <div className="flex items-center gap-2 px-2 py-1 border border-primary/40 bg-primary/5 text-[11px]"
             data-testid="cc-tl-filter-pill">
          <span>Filtered by <b>{activeFilter.label}</b> · {activeFilter.itemIds.length} items</span>
          <button className="ml-auto text-[11px] underline decoration-dotted underline-offset-2 hover:text-primary flex items-center gap-1"
                  onClick={() => setActiveFilter(null)}
                  data-testid="cc-tl-filter-clear">
            <X className="w-3 h-3" /> Clear filter
          </button>
        </div>
      )}

      {/* Insights panel — anchored to the CURRENT (initialMonth) so
          past/future compare doesn't change what's analysed. */}
      {(monthsData[initialMonth] || []).length > 0 && (
        <TimelineInsights
          items={monthsData[initialMonth] || []}
          year={Number(initialMonth.split("-")[0])}
          month={Number(initialMonth.split("-")[1])}
          wsId={wsId}
          workspaceName={workspaceName}
          onFilterChange={setActiveFilter}
        />
      )}

      {/* Body — in fullscreen it fills the viewport (flex-1 + inner
          scroll); otherwise it flows naturally so brands never get
          cropped by an arbitrary height constraint. */}
      <div className={`flex gap-3 ${fullscreen ? "flex-1 min-h-0" : ""}`}>
        <div className={`flex-1 min-w-0 space-y-4 ${fullscreen ? "overflow-auto" : ""}`}>
          {monthList.map((m) => (
            <TimelineMonthGrid
              key={m}
              month={m}
              items={applyFilter(monthsData[m] || [])}
              gridlines={gridlines}
              showCategoryLabels={showCategoryLabels}
              showNumbering={showNumbering}
              expBrands={expBrands} toggleBrand={toggleBrand}
              expPkgs={expPkgs} togglePkg={togglePkg}
              expCats={expCats} toggleCat={toggleCat}
              dragging={dragging} setDragging={setDragging}
              dragOver={dragOver} setDragOver={setDragOver}
              reschedule={reschedule}
              onOpen={onOpen} onOpenTask={onOpenTask} members={members} onReloaded={loadAll}
              groupMode={groupMode}
              isCompareView={isCompareView}
              isCurrentMonth={m === initialMonth}
              importantDays={importantDays}
              colWidthOverride={colWidthOverride}
            />
          ))}
        </div>

        {/* Assignee pending panel — sticky so it stays visible while
            the timeline scrolls, both in and out of fullscreen. */}
        <div className={fullscreen ? "" : "sticky top-4 self-start max-h-[calc(100vh-8rem)] overflow-auto"}>
          <AssigneePanel items={monthsData[initialMonth] || []} onOpen={onOpen} onOpenTask={onOpenTask} members={members} onReloaded={loadAll} />
        </div>
      </div>

      {carryOpen && (
        <CarryForwardDialog
          wsId={wsId}
          defaultTarget={initialMonth}
          onClose={() => setCarryOpen(false)}
          onApplied={loadAll}
        />
      )}
      {respreadOpen && (
        <RespreadDatesDialog
          wsId={wsId}
          month={initialMonth}
          onClose={() => setRespreadOpen(false)}
          onApplied={loadAll}
        />
      )}
    </div>
  );
}

// ---- Month grid (renders one month's brand/package/category rows) ----

function TimelineMonthGrid({
  month, items, gridlines, showCategoryLabels, showNumbering,
  expBrands, toggleBrand, expPkgs, togglePkg, expCats, toggleCat,
  dragging, setDragging, dragOver, setDragOver, reschedule,
  onOpen, onOpenTask, members, onReloaded, groupMode = "category", isCompareView, isCurrentMonth, importantDays = [], colWidthOverride,
}) {
  const days = daysInMonth(month);
  const [y, m] = month.split("-").map(Number);

  // Iter · Feb 2026 — Weekend detection + day-of-week letters.
  const DOW_LETTERS = ["S", "M", "T", "W", "T", "F", "S"];
  const dayInfo = useMemo(() => {
    const arr = [];
    for (let d = 1; d <= days; d++) {
      const dt = new Date(y, m - 1, d);
      const dow = dt.getDay();
      arr.push({ d, dow, isWeekend: dow === 0 || dow === 6, letter: DOW_LETTERS[dow] });
    }
    return arr;
  }, [y, m, days]);

  // Which brand names have a deliverable scheduled on each day (for
  // the Important-days hover card).
  const brandsByDay = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const d = parseInt((it.planned_date || "").slice(8), 10);
      if (!d) continue;
      const set = map.get(d) || new Set();
      set.add(it.brand_name || "—");
      map.set(d, set);
    }
    return map;
  }, [items]);

  // Which important days fall on each day of the month.
  const impDaysByDay = useMemo(() => {
    const map = new Map();
    for (const d of importantDays) {
      if (!d?.date || !d.date.startsWith(month)) continue;
      const dayNum = parseInt(d.date.slice(8), 10);
      const arr = map.get(dayNum) || [];
      arr.push(d);
      map.set(dayNum, arr);
    }
    return map;
  }, [importantDays, month]);

  // Iter · Feb 2026 R230 — Column-level background rules requested by user:
  //   • Weekend            → light gray
  //   • Special (imp) day  → light yellow
  //   • Special + weekend  → light orange (loud both-signal)
  // Applied uniformly to the DoW header, day-number header, the
  // Important-days row cells and every deliverable day cell so the
  // vertical stripe reads at-a-glance top-to-bottom.
  const dayBg = (dayNum) => {
    const di = dayInfo[dayNum - 1];
    if (!di) return "";
    const isWkend = di.isWeekend;
    const isSpecial = (impDaysByDay.get(dayNum) || []).length > 0;
    if (isWkend && isSpecial) return "bg-orange-100 dark:bg-orange-500/20";
    if (isSpecial) return "bg-yellow-100 dark:bg-yellow-500/15";
    if (isWkend) return "bg-gray-100 dark:bg-muted/40";
    return "";
  };

  // Column width scales with busiest day.
  const maxItemsPerDay = useMemo(() => {
    let mx = 0;
    for (let d = 1; d <= days; d++) {
      const n = items.filter((it) => parseInt((it.planned_date || "").slice(8), 10) === d).length;
      if (n > mx) mx = n;
    }
    return mx;
  }, [items, days]);
  const perDayWidth = colWidthOverride != null
    ? Math.max(24, colWidthOverride)
    : Math.min(60, Math.max(24, 18 + maxItemsPerDay * 6));
  const gridCols = `240px repeat(${days}, minmax(${perDayWidth}px, 1fr))`;

  // Group items brand → package → (category|title) → items[]
  const byBrand = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      const bKey = it.brand_id || it.brand_name || "_none";
      if (!m.has(bKey)) m.set(bKey, { key: bKey, name: it.brand_name || "—", pkgs: new Map() });
      const b = m.get(bKey);
      const pKey = it.package_id || it.package_name || "_none";
      if (!b.pkgs.has(pKey)) b.pkgs.set(pKey, { key: pKey, name: it.package_name || "— No package —", cats: new Map(), items: [] });
      const p = b.pkgs.get(pKey);
      p.items.push(it);
      // Feb 2026 · R257 — third level pivots between category and
      // deliverable title (base_name) based on toolbar toggle.
      const cKey = groupMode === "title"
        ? (it.line_id || it.base_name || it.category || "Uncategorised")
        : (it.category || "Uncategorised");
      const cName = groupMode === "title"
        ? (it.base_name || it.category || "Uncategorised")
        : (it.category || "Uncategorised");
      if (!p.cats.has(cKey)) p.cats.set(cKey, { key: cKey, name: cName, color: it.activity_type_color, items: [] });
      p.cats.get(cKey).items.push(it);
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [items, groupMode]);

  const borderCls = gridlines ? "border-l border-border/40" : "";
  const rowBorder = gridlines ? "border-t border-border" : "border-t border-transparent";

  // R268~8 · Feb 2026 — Numbering helper: given a list of items in a
  // row's scope (a brand row, package row, or category row for the
  // month), produces { itemId → "index/total" } sorted by planned_date
  // (chronological, cancelled items excluded from the total).
  const buildNumberMap = (rowItems) => {
    if (!showNumbering) return null;
    const sorted = [...(rowItems || [])]
      .filter((x) => x && x.status !== "cancelled")
      .sort((a, b) => (a.planned_date || "").localeCompare(b.planned_date || ""));
    const total = sorted.length;
    const map = new Map();
    sorted.forEach((it, idx) => map.set(it.id, `${idx + 1}/${total}`));
    return map;
  };

  const renderDot = (it, numberLabel = null) => {
    const c = dotColour(it);
    // R268~8 · Feb 2026 — When numbering is on we replace the tiny
    // circle with a filled pill that shows the "N/T" index. The
    // background stays the deliverable's dot colour (already dark
    // enough per the palette) and text is forced white for contrast.
    const withNumber = !!numberLabel;
    return (
      <HoverCard key={it.id} openDelay={200} closeDelay={80}>
        <HoverCardTrigger asChild>
          <button
            onClick={() => onOpen(it)}
            draggable
            onDragStart={(e) => {
              setDragging({ id: it.id, sourceMonth: month });
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", it.id);
            }}
            onDragEnd={() => { setDragging(null); setDragOver(null); }}
            className={`inline-flex items-center gap-1 min-w-0 max-w-full ${dragging?.id === it.id ? "opacity-40" : ""}`}
            title={(() => {
              const topic = it.linked_bucket?.topic_title || it.topic;
              const base = `${it.name}${topic ? ` · ${topic}` : ""} — ${it.category}`;
              return `${base}${it.primary_assignee_name ? ` · owner: ${it.primary_assignee_name}` : ""}${numberLabel ? ` · Content #${numberLabel}` : ""}`;
            })()}
            data-testid={`cc-tl-dot-${it.id}`}>
            {withNumber ? (
              <span
                className="shrink-0 h-[14px] rounded-sm px-1 text-[8.5px] font-mono font-semibold leading-[14px] tracking-tight whitespace-nowrap text-white shadow-[inset_0_0_0_1px_rgba(0,0,0,0.25)]"
                style={{ background: c }}
                data-testid={`cc-tl-num-${it.id}`}
              >{numberLabel}</span>
            ) : (
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: c }} />
            )}
            {showCategoryLabels && (
              <span className="text-[9px] truncate max-w-[70px]" style={{ color: c }}>{it.category}</span>
            )}
            {/* R268www · Feb 2026 — Owner initial badge (single letter)
                so PMs see who owns each dot even on a dense month view.
                Full name lives in the title/hover card. */}
            {it.primary_assignee_name && (
              <span className="text-[9px] font-semibold w-3.5 h-3.5 inline-flex items-center justify-center rounded-full bg-primary/15 text-primary shrink-0"
                    title={`Owner: ${it.primary_assignee_name}`}
                    data-testid={`cc-tl-owner-${it.id}`}>
                {it.primary_assignee_name.trim()[0].toUpperCase()}
              </span>
            )}
          </button>
        </HoverCardTrigger>
        <HoverCardContent side="right" align="start" className="w-[560px] p-0 rounded-none">
          <CalendarHoverCard it={it} onOpen={onOpen} onOpenTask={onOpenTask} members={members} onReassigned={onReloaded} />
        </HoverCardContent>
      </HoverCard>
    );
  };

  const dayCell = (dayItems, dayNum, numberMap = null) => {
    const isOver = dragOver?.month === month && dragOver?.day === dayNum && dragging;
    const bg = dayBg(dayNum);
    return (
      <div
        key={`cell-${month}-${dayNum}`}
        onDragOver={(e) => { if (dragging) { e.preventDefault(); setDragOver({ month, day: dayNum }); } }}
        onDragLeave={() => { if (dragOver?.day === dayNum) setDragOver(null); }}
        onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain") || dragging?.id; setDragOver(null); reschedule(id, month, dayNum); }}
        className={`min-h-9 min-w-0 overflow-hidden ${borderCls} flex flex-col items-start justify-start gap-0.5 py-0.5 px-0.5 ${bg} ${isOver ? "bg-primary/20 outline outline-1 outline-primary" : ""}`}
        data-testid={`cc-tl-cell-${month}-${dayNum}`}>
        {dayItems.map((it) => renderDot(it, numberMap ? numberMap.get(it.id) : null))}
      </div>
    );
  };

  const dayRange = Array.from({ length: days }, (_, i) => i + 1);

  return (
    <div className={`border ${isCurrentMonth ? "border-primary/60 shadow-[0_0_0_1px_hsl(var(--primary)/0.4)]" : "border-border"}`}>
      {isCompareView && (
        <div className={`px-3 py-1.5 border-b border-border text-[11px] font-semibold uppercase tracking-widest flex items-center gap-2 ${isCurrentMonth ? "bg-primary/20 text-primary" : "bg-primary/10 text-primary"}`}
             data-testid={`cc-tl-month-divider-${month}`}>
          <span>{monthLabel(month)}</span>
          {isCurrentMonth && (
            <span className="chip text-[9px] bg-primary text-primary-foreground border-primary px-1.5 py-0 normal-case tracking-normal font-bold"
                  data-testid={`cc-tl-current-badge-${month}`}>
              Current
            </span>
          )}
          {byBrand.length === 0 && (
            <span className="text-[10px] text-muted-foreground font-normal normal-case tracking-normal">· no deliverables scheduled</span>
          )}
        </div>
      )}
      {/* In compare mode, collapse fully empty months to just the
          divider — saves a lot of vertical space when browsing 6
          months side-by-side. */}
      {isCompareView && byBrand.length === 0 ? null : (
      <div className={`overflow-auto ${isCompareView ? "" : "max-h-[calc(100vh-260px)]"}`}
           data-testid={`cc-tl-scroll-${month}`}>
        <div className="min-w-[900px]">
          {/* Sticky day-number header row + DoW letter + weekend shade. */}
          <div className={`grid sticky ${isCompareView ? "top-7" : "top-0"} z-[20] bg-background`}
               style={{ gridTemplateColumns: gridCols }}>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold px-2 py-1 sticky left-0 z-[30] bg-background cc-sticky-col border-r border-border shadow-[2px_0_4px_-1px_rgba(0,0,0,0.08)] row-span-2"
                 style={{ gridRow: "span 2" }}>
              Brand · Package · Category
            </div>
            {/* Row 1 — Day-of-week letter */}
            {dayInfo.map((di) => (
              <div key={`dow-${di.d}`}
                   className={`text-[8px] text-center font-semibold py-0.5 ${borderCls} ${dayBg(di.d)} ${di.isWeekend ? "text-muted-foreground" : "text-muted-foreground/70"}`}>
                {di.letter}
              </div>
            ))}
          </div>
          <div className={`grid sticky ${isCompareView ? "top-[42px]" : "top-4"} z-[20] bg-background`}
               style={{ gridTemplateColumns: gridCols }}>
            <div className="sticky left-0 z-[30] bg-background cc-sticky-col border-r border-border shadow-[2px_0_4px_-1px_rgba(0,0,0,0.08)]" />
            {/* Row 2 — Day number */}
            {dayInfo.map((di) => (
              <div key={`num-${di.d}`}
                   className={`text-[9px] text-center text-muted-foreground font-mono py-1 ${borderCls} ${dayBg(di.d)}`}>
                {di.d}
              </div>
            ))}
          </div>

          {/* Important-days row */}
          {impDaysByDay.size > 0 && (
            <div className="grid border-t border-b border-amber-500/30 bg-amber-500/5"
                 style={{ gridTemplateColumns: gridCols }}
                 data-testid={`cc-tl-impdays-row-${month}`}>
              <div className="sticky left-0 z-[15] bg-background cc-sticky-col border-r border-border shadow-[2px_0_4px_-1px_rgba(0,0,0,0.08)] px-2 py-1 text-[9px] uppercase tracking-widest text-amber-700 font-semibold">
                Important days
              </div>
              {dayInfo.map((di) => {
                const impList = impDaysByDay.get(di.d) || [];
                if (impList.length === 0) {
                  return <div key={di.d} className={`${borderCls} ${di.isWeekend ? "bg-muted/40" : ""}`} />;
                }
                return (
                  <HoverCard key={di.d} openDelay={80} closeDelay={80}>
                    <HoverCardTrigger asChild>
                      <button className={`${borderCls} flex items-center justify-center py-0.5 hover:bg-amber-500/20 cursor-help`}
                              data-testid={`cc-tl-impday-${month}-${di.d}`}>
                        <span className="text-[10px] leading-none">{impList[0].emoji || "★"}</span>
                        {impList.length > 1 && <span className="text-[8px] text-amber-700 ml-0.5">+{impList.length - 1}</span>}
                      </button>
                    </HoverCardTrigger>
                    <HoverCardContent side="top" align="center" className="w-72 p-0 rounded-none"
                                       data-testid={`cc-tl-impday-hover-${month}-${di.d}`}>
                      <div className="px-3 py-2 border-b border-border bg-amber-500/10">
                        <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">
                          {new Date(y, m - 1, di.d).toLocaleDateString("en", { weekday: "long", month: "short", day: "numeric" })}
                        </div>
                      </div>
                      <ul className="divide-y divide-border">
                        {impList.map((imp, i) => (
                          <li key={i} className="px-3 py-2 text-[11px]">
                            <div className="font-medium flex items-center gap-1.5">
                              {imp.emoji && <span>{imp.emoji}</span>}
                              <span>{imp.name}</span>
                              {imp.category && <span className="text-[9px] text-muted-foreground uppercase tracking-widest">· {imp.category}</span>}
                            </div>
                            {imp.description && <div className="text-[10px] text-muted-foreground mt-0.5">{imp.description}</div>}
                          </li>
                        ))}
                        {(brandsByDay.get(di.d)?.size || 0) > 0 && (
                          <li className="px-3 py-2 text-[10px]">
                            <div className="uppercase tracking-widest text-muted-foreground font-semibold mb-0.5">
                              {brandsByDay.get(di.d).size} brand{brandsByDay.get(di.d).size === 1 ? "" : "s"} using this day
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {[...brandsByDay.get(di.d)].map((bn) => (
                                <span key={bn} className="chip text-[9px] bg-primary/10 text-primary border-primary/30 px-1.5 py-0">{bn}</span>
                              ))}
                            </div>
                          </li>
                        )}
                      </ul>
                    </HoverCardContent>
                  </HoverCard>
                );
              })}
            </div>
          )}

          {byBrand.length === 0 && (
            <div className="text-sm text-muted-foreground border border-dashed border-border p-6 text-center m-2">No items this month.</div>
          )}

          {byBrand.map((b) => {
            const bExpanded = expBrands.has(`${month}/${b.key}`);
            const brandItems = [...b.pkgs.values()].flatMap((p) => p.items);
            const bDone = brandItems.filter((x) => x.status === "published").length;
            const bTotal = brandItems.filter((x) => x.status !== "cancelled").length;
            const bPend = bTotal - bDone;
            return (
              <React.Fragment key={b.key}>
                {/* Brand row */}
                <div className={`grid ${rowBorder} items-center hover:bg-muted/20`}
                     style={{ gridTemplateColumns: gridCols }}
                     data-testid={`cc-tl-brand-${month}-${b.key}`}>
                  <button className="px-2 py-2 text-xs font-semibold truncate text-left flex items-center gap-1.5 sticky left-0 z-[15] bg-background cc-sticky-col border-r border-border shadow-[2px_0_4px_-1px_rgba(0,0,0,0.08)]"
                          onClick={() => toggleBrand(`${month}/${b.key}`)}
                          data-testid={`cc-tl-brand-toggle-${month}-${b.key}`}>
                    <span className="text-muted-foreground text-[10px] w-3 shrink-0">{bExpanded ? "▼" : "▶"}</span>
                    <span className="truncate flex-1 min-w-0">{b.name}</span>
                    <span className="text-[9px] font-mono text-muted-foreground shrink-0">
                      <span className="text-emerald-700">{bDone}</span>/<span>{bTotal}</span>
                      {bPend > 0 && <span className="ml-1 text-rose-600">·{bPend}p</span>}
                    </span>
                  </button>
                  {(() => {
                    // R268~8 · Feb 2026 — Number all brand-row items
                    // chronologically once per render; each day cell
                    // then just looks up the pre-computed label.
                    const nm = buildNumberMap(brandItems);
                    return dayRange.map((d) => dayCell(
                      brandItems.filter((it) => parseInt((it.planned_date || "").slice(8), 10) === d), d, nm
                    ));
                  })()}
                </div>

                {bExpanded && [...b.pkgs.values()].sort((a, b2) => a.name.localeCompare(b2.name)).map((p) => {
                  const pKey = `${month}/${b.key}/${p.key}`;
                  const pExpanded = expPkgs.has(pKey);
                  const pDone = p.items.filter((x) => x.status === "published").length;
                  const pTotal = p.items.filter((x) => x.status !== "cancelled").length;
                  const pPend = pTotal - pDone;
                  return (
                    <React.Fragment key={p.key}>
                      {/* Package row */}
                      <div className={`grid ${rowBorder} items-center bg-muted/10 hover:bg-muted/25`}
                           style={{ gridTemplateColumns: gridCols }}
                           data-testid={`cc-tl-pkg-${month}-${b.key}-${p.key}`}>
                        <button className="px-2 pl-6 py-1.5 text-[11px] truncate text-left flex items-center gap-1.5 sticky left-0 z-[15] bg-background cc-sticky-col border-r border-border shadow-[2px_0_4px_-1px_rgba(0,0,0,0.08)]"
                                onClick={() => togglePkg(pKey)}
                                data-testid={`cc-tl-pkg-toggle-${month}-${b.key}-${p.key}`}>
                          <span className="text-muted-foreground text-[10px] w-3 shrink-0">{pExpanded ? "▼" : "▶"}</span>
                          <PackageHoverPreview pid={p.key} disabled={!p.key || p.key === "_none"}>
                            <span className="chip text-[9.5px] bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30 truncate cursor-help"
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`cc-tl-pkg-name-${month}-${b.key}-${p.key}`}>
                              {p.name}
                            </span>
                          </PackageHoverPreview>
                          <span className="text-[9px] font-mono text-muted-foreground ml-auto shrink-0">
                            <span className="text-emerald-700">{pDone}</span>/<span>{pTotal}</span>
                            {pPend > 0 && <span className="ml-1 text-rose-600">·{pPend}p</span>}
                          </span>
                        </button>
                        {(() => {
                          const nm = buildNumberMap(p.items);
                          return dayRange.map((d) => dayCell(
                            p.items.filter((it) => parseInt((it.planned_date || "").slice(8), 10) === d), d, nm
                          ));
                        })()}
                      </div>

                      {pExpanded && [...p.cats.values()].sort((a, b2) => a.name.localeCompare(b2.name)).map((cat) => {
                        const cKey = `${month}/${b.key}/${p.key}/${cat.key}`;
                        const cDone = cat.items.filter((x) => x.status === "published").length;
                        const cTotal = cat.items.filter((x) => x.status !== "cancelled").length;
                        const cPend = cTotal - cDone;
                        return (
                          <div key={cat.key}
                               className={`grid ${rowBorder} items-center bg-muted/5 hover:bg-muted/20`}
                               style={{ gridTemplateColumns: gridCols }}
                               data-testid={`cc-tl-cat-${month}-${b.key}-${p.key}-${cat.key}`}>
                            <div className="px-2 pl-10 py-1 text-[10.5px] truncate flex items-center gap-1.5 sticky left-0 z-[15] bg-background cc-sticky-col border-r border-border shadow-[2px_0_4px_-1px_rgba(0,0,0,0.08)]">
                              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: cat.color || "#8b5cf6" }} />
                              <span className="truncate flex-1 min-w-0">{cat.name}</span>
                              <span className="text-[9px] font-mono text-muted-foreground shrink-0">
                                <span className="text-emerald-700">{cDone}</span>/<span>{cTotal}</span>
                                {cPend > 0 && <span className="ml-1 text-rose-600">·{cPend}p</span>}
                              </span>
                            </div>
                            {(() => {
                              const nm = buildNumberMap(cat.items);
                              return dayRange.map((d) => dayCell(
                                cat.items.filter((it) => parseInt((it.planned_date || "").slice(8), 10) === d), d, nm
                              ));
                            })()}
                            {/* Just to silence eslint hooks-rule; cKey used for testid */}
                            <span className="hidden" data-testid={cKey} />
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
}

// ---- Assignee pending panel ----
// Rich hover card body for a single pending task row. Shown on hover of the
// task title in the Timeline's "Pending by assignee" panel. Surfaces full
// context (client/brand, project, deliverable, topic, due date, status) so
// PMs can triage without opening the task.
function PendingTaskHoverBody({ t, onOpen, onOpenTask }) {
  const c = t._activity_type_color || "#7c3aed";
  const today = new Date().toISOString().slice(0, 10);
  const overdue = t.due_date && t.due_date < today && t.status !== "done";
  return (
    <div className="text-xs" data-testid={`cc-tl-pending-hover-body-${t.id}`}>
      <div className="px-3 py-2 border-b border-border"
           style={{ background: c + "10", borderLeft: `4px solid ${c}` }}>
        <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">Task</div>
        <div className="font-semibold break-words leading-snug" data-testid="cc-tl-pending-hover-title">
          {t.title || "Task"}
        </div>
      </div>
      <div className="p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">Client</div>
            <div className="font-medium truncate" title={t._brand_name || "—"}
                 data-testid="cc-tl-pending-hover-client">
              {t._brand_name || "—"}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">Project</div>
            <div className="font-medium truncate" title={t._project_name || "—"}
                 data-testid="cc-tl-pending-hover-project">
              {t._project_name || "—"}
            </div>
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">Deliverable</div>
          <div className="font-medium break-words" data-testid="cc-tl-pending-hover-deliverable">
            {t._item_name || "—"}
            {t._category && <span className="text-muted-foreground"> · {t._category}</span>}
          </div>
        </div>
        {t._topic && (
          <div>
            <div className="text-[9px] uppercase tracking-widest text-emerald-700 font-bold">Topic</div>
            <div className={`break-words ${t._topic_linked ? "font-bold text-emerald-800" : "font-semibold italic text-muted-foreground"}`}
                 data-testid="cc-tl-pending-hover-topic">
              {t._topic}
            </div>
          </div>
        )}
        <div className="grid grid-cols-3 gap-2 pt-1 border-t border-border">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">Due</div>
            <div className={`font-mono text-[11px] ${overdue ? "text-rose-600 font-semibold" : ""}`}
                 data-testid="cc-tl-pending-hover-due">
              {t.due_date || "—"}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">Status</div>
            <div className={`text-[11px] font-semibold ${
              t.status === "blocked" ? "text-rose-700" :
              t.status === "in_progress" ? "text-blue-700" :
              t.status === "review" ? "text-amber-700" :
              "text-foreground"
            }`} data-testid="cc-tl-pending-hover-status">
              {(t.status || "open").replace(/_/g, " ")}
            </div>
          </div>
          {t.est_hours ? (
            <div>
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">Est.</div>
              <div className="font-mono text-[11px]">{t.est_hours}h</div>
            </div>
          ) : <div />}
        </div>
        <div className="flex items-center justify-between pt-1 border-t border-border">
          <button type="button"
                  onClick={() => onOpen?.({ id: t._item_id })}
                  className="text-[10px] text-muted-foreground hover:text-primary hover:underline"
                  data-testid="cc-tl-pending-hover-open-deliverable">
            Open deliverable →
          </button>
          <button type="button"
                  onClick={() => onOpenTask?.(t.id)}
                  className="text-[10px] font-bold text-primary hover:underline"
                  data-testid="cc-tl-pending-hover-open-task">
            Open task →
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Assignee pending panel ----
function AssigneePanel({ items, onOpen, onOpenTask, members = [], onReloaded }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const [modalOpen, setModalOpen] = useState(false);

  // Pending-task roster indexed by assignee + a flat list for the modal.
  // Each task is enriched with the parent deliverable so the modal can
  // group by project without a second lookup.
  const { byAssignee, flatTasks } = useMemo(() => {
    const map = new Map();
    const flat = [];
    for (const it of items || []) {
      if (it.status === "published" || it.status === "cancelled") continue;
      for (const t of (it.tasks || [])) {
        if (t.status === "done" || t.status === "cancelled") continue;
        const key = t.assignee_id || "_unassigned";
        const name = t.assignee_name || "Unassigned";
        const row = map.get(key) || { key, name, count: 0, tasks: [] };
        const enriched = {
          ...t,
          _item_id: it.id,
          _item_name: it.name,
          _planned_date: it.planned_date,
          _project_id: it.project_id,
          _project_name: it.project_name
            || (it.project_names || [])[0]
            || null,
          _brand_name: it.brand_name,
          _category: it.category,
          // Feb 2026 — Enrich with Topic/Status/visuals so the Pending-by-Assignee
          // hover card can show full context (brand, project, topic, deliverable).
          _topic: it.linked_bucket?.topic_title || it.topic || null,
          _topic_linked: Boolean(it.linked_bucket?.topic_title),
          _item_status: it.status,
          _activity_type_color: it.activity_type_color || "#7c3aed",
          _activity_type_icon: it.activity_type_icon,
          _item_preview_uri: it.linked_bucket?.preview_uri || null,
        };
        row.count += 1;
        row.tasks.push(enriched);
        map.set(key, row);
        flat.push({ ...enriched, _assignee_id: key, _assignee_name: name });
      }
    }
    // Sort each assignee's task list by due date (then by title).
    for (const row of map.values()) {
      row.tasks.sort((a, b) => {
        const da = a.due_date || "9999-99-99";
        const db = b.due_date || "9999-99-99";
        if (da !== db) return da < db ? -1 : 1;
        return (a.title || "").localeCompare(b.title || "");
      });
    }
    const list = [...map.values()].sort((a, b) => b.count - a.count);
    return { byAssignee: list, flatTasks: flat };
  }, [items]);

  const toggle = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const totalCount = byAssignee.reduce((s, a) => s + a.count, 0);

  return (
    <>
      <aside className="w-56 shrink-0 border border-border bg-muted/10 self-start" data-testid="cc-tl-assignee-panel">
        <div className="px-3 py-2 border-b border-border bg-muted/40 flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Pending by assignee
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">Tasks not done, first month</div>
          </div>
          {totalCount > 0 && (
            <button type="button"
                    onClick={() => setModalOpen(true)}
                    className="text-[10px] text-primary hover:underline whitespace-nowrap shrink-0 mt-0.5"
                    title="Open full list in a modal"
                    data-testid="cc-tl-assignee-expand-modal">
              Expand ↗
            </button>
          )}
        </div>
        {byAssignee.length === 0 && (
          <div className="px-3 py-4 text-[11px] text-muted-foreground text-center">Nothing pending 🎉</div>
        )}
        <ul className="divide-y divide-border">
          {byAssignee.map((a) => {
            const open = expanded.has(a.key);
            return (
              <li key={a.key} data-testid={`cc-tl-assignee-${a.key}`}>
                <button type="button"
                        onClick={() => toggle(a.key)}
                        className="w-full px-3 py-1.5 flex items-center justify-between text-[11px] hover:bg-muted/40"
                        data-testid={`cc-tl-assignee-toggle-${a.key}`}>
                  <span className="flex items-center gap-1 min-w-0">
                    <span className="text-muted-foreground text-[10px] w-3 shrink-0">{open ? "▼" : "▶"}</span>
                    <span className="truncate">{a.name}</span>
                  </span>
                  <span className={`font-mono shrink-0 ${a.count >= 8 ? "text-rose-600 font-semibold" : a.count >= 4 ? "text-amber-700" : "text-muted-foreground"}`}>
                    {a.count}
                  </span>
                </button>
                {open && (
                  <ul className="pl-5 pr-2 pb-1.5 space-y-0.5 bg-muted/5" data-testid={`cc-tl-assignee-tasks-${a.key}`}>
                    {a.tasks.map((t) => (
                      <li key={t.id} className="text-[10.5px] flex items-start gap-1.5">
                        {t.due_date && (
                          <span className={`font-mono text-[9.5px] shrink-0 mt-0.5 ${
                            t.due_date < new Date().toISOString().slice(0, 10)
                              ? "text-rose-600 font-semibold"
                              : "text-muted-foreground"
                          }`} title={`Due ${t.due_date}`}>
                            {t.due_date.slice(5)}
                          </span>
                        )}
                        <HoverCard openDelay={180} closeDelay={80}>
                          <HoverCardTrigger asChild>
                            <button type="button"
                                    onClick={() => onOpenTask?.(t.id)}
                                    className="text-left hover:text-primary hover:underline min-w-0 flex-1 truncate"
                                    data-testid={`cc-tl-assignee-task-${t.id}`}>
                              {t.title || "Task"}
                            </button>
                          </HoverCardTrigger>
                          <HoverCardContent side="right" align="start"
                                            className="w-80 p-0 border border-border shadow-xl rounded-none"
                                            data-testid={`cc-tl-assignee-task-hover-${t.id}`}>
                            <PendingTaskHoverBody t={t} onOpen={onOpen} onOpenTask={onOpenTask} />
                          </HoverCardContent>
                        </HoverCard>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </aside>

      {modalOpen && (
        <PendingTasksDialog
          tasks={flatTasks}
          members={members}
          onReloaded={onReloaded}
          onClose={() => setModalOpen(false)}
          onOpenTask={onOpenTask}
          onOpenItem={onOpen}
        />
      )}
    </>
  );
}

// ---- Pending tasks dialog (grouped by assignee or project) ----
function PendingTasksDialog({ tasks, members = [], onReloaded, onClose, onOpenTask, onOpenItem }) {
  const [groupBy, setGroupBy] = useState("assignee"); // assignee | project
  const [q, setQ] = useState("");
  // Iter · Feb 2026 — Bulk reassign. `selected` holds task ids; the
  // Reassign toolbar appears when at least one row is ticked.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkTarget, setBulkTarget] = useState("");
  const [applying, setApplying] = useState(false);

  const toggleSel = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleGroupSel = (gTasks, allSel) => setSelected((prev) => {
    const next = new Set(prev);
    for (const t of gTasks) {
      if (allSel) next.delete(t.id); else next.add(t.id);
    }
    return next;
  });

  const applyBulkReassign = async () => {
    if (!bulkTarget || selected.size === 0) return;
    setApplying(true);
    let ok = 0, fail = 0;
    // Sequential patches — small N (bulk reassign of a handful of tasks),
    // sequential keeps error messaging clean and avoids rate-limit spikes
    // on PATCH /tasks/{tid}. Each failure is counted but doesn't abort
    // the rest of the batch.
    for (const tid of selected) {
      try {
        const body = bulkTarget === "_unassigned"
          ? { assignee_id: null }
          : { assignee_id: bulkTarget };
        await api.patch(`/tasks/${tid}`, body);
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setApplying(false);
    if (ok > 0) toast.success(`Reassigned ${ok} task${ok === 1 ? "" : "s"}${fail > 0 ? ` · ${fail} failed` : ""}`);
    else toast.error(`Failed to reassign ${fail} task${fail === 1 ? "" : "s"}`);
    setSelected(new Set());
    setBulkTarget("");
    onReloaded?.();
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return tasks;
    return tasks.filter((t) => {
      const hay = [t.title, t._assignee_name, t._project_name, t._item_name, t._brand_name, t._category]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(s);
    });
  }, [tasks, q]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const t of filtered) {
      const key = groupBy === "project"
        ? (t._project_id || "_none")
        : (t._assignee_id || "_unassigned");
      const label = groupBy === "project"
        ? (t._project_name || "No project")
        : (t._assignee_name || "Unassigned");
      const row = map.get(key) || { key, label, tasks: [] };
      row.tasks.push(t);
      map.set(key, row);
    }
    for (const r of map.values()) {
      r.tasks.sort((a, b) => {
        const da = a.due_date || "9999-99-99";
        const db = b.due_date || "9999-99-99";
        if (da !== db) return da < db ? -1 : 1;
        return (a.title || "").localeCompare(b.title || "");
      });
    }
    return [...map.values()].sort((a, b) => b.tasks.length - a.tasks.length);
  }, [filtered, groupBy]);

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl w-[95vw] p-0 rounded-none" data-testid="cc-tl-pending-modal">
        <DialogHeader className="px-5 py-3 border-b border-border bg-muted/30">
          <DialogTitle className="text-base">Pending tasks · {tasks.length}</DialogTitle>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <div className="relative">
              <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Search title, assignee, project…"
                     className="h-8 w-72 rounded-none border border-border bg-background px-2 text-xs"
                     data-testid="cc-tl-pending-search" />
            </div>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold ml-2">Group by</span>
            <div className="flex items-center border border-border rounded-none">
              <button type="button"
                      onClick={() => setGroupBy("assignee")}
                      className={`h-8 px-3 text-xs ${groupBy === "assignee" ? "bg-primary/10 text-primary font-semibold" : "hover:bg-muted/60 text-muted-foreground"}`}
                      data-testid="cc-tl-pending-group-assignee">Assignee</button>
              <button type="button"
                      onClick={() => setGroupBy("project")}
                      className={`h-8 px-3 text-xs border-l border-border ${groupBy === "project" ? "bg-primary/10 text-primary font-semibold" : "hover:bg-muted/60 text-muted-foreground"}`}
                      data-testid="cc-tl-pending-group-project">Project</button>
            </div>
            <span className="text-[11px] text-muted-foreground ml-auto">
              {filtered.length} of {tasks.length} shown
            </span>
          </div>
          {/* Bulk reassign toolbar — only shown when at least one row is
              ticked. Lets a manager push every selected task onto a new
              owner (or to Unassigned) with a single click. */}
          {selected.size > 0 && (
            <div className="flex items-center gap-2 mt-2 px-3 py-1.5 bg-primary/10 border border-primary/30"
                 data-testid="cc-tl-pending-bulk-toolbar">
              <span className="text-[11px] font-semibold text-primary">
                {selected.size} selected
              </span>
              <button type="button"
                      onClick={() => setSelected(new Set())}
                      className="text-[10px] text-primary hover:underline"
                      data-testid="cc-tl-pending-bulk-clear">
                Clear
              </button>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold ml-2">Reassign to</span>
              <Select value={bulkTarget} onValueChange={setBulkTarget}>
                <SelectTrigger className="h-8 w-56 rounded-none text-xs" data-testid="cc-tl-pending-bulk-target">
                  <SelectValue placeholder="Pick a teammate…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_unassigned" data-testid="cc-tl-pending-bulk-opt-unassigned">— Unassigned —</SelectItem>
                  {members.map((m) => (
                    <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}
                                data-testid={`cc-tl-pending-bulk-opt-${m.user_id || m.id}`}>
                      {m.name || m.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" className="h-8 rounded-none"
                      onClick={applyBulkReassign}
                      disabled={!bulkTarget || applying}
                      data-testid="cc-tl-pending-bulk-apply">
                {applying ? "Reassigning…" : "Apply"}
              </Button>
            </div>
          )}
        </DialogHeader>
        <div className="max-h-[70vh] overflow-auto px-5 py-3 space-y-4">
          {groups.length === 0 && (
            <div className="text-sm text-muted-foreground py-10 text-center">No pending tasks match this search.</div>
          )}
          {groups.map((g) => {
            const gIds = g.tasks.map((t) => t.id);
            const allSel = gIds.length > 0 && gIds.every((id) => selected.has(id));
            const someSel = !allSel && gIds.some((id) => selected.has(id));
            return (
            <div key={g.key} className="border border-border" data-testid={`cc-tl-pending-group-${g.key}`}>
              <div className="px-3 py-2 bg-muted/40 border-b border-border flex items-center justify-between gap-2">
                <label className="flex items-center gap-2 cursor-pointer min-w-0">
                  <input type="checkbox"
                         checked={allSel}
                         ref={(el) => { if (el) el.indeterminate = someSel; }}
                         onChange={() => toggleGroupSel(g.tasks, allSel)}
                         className="shrink-0"
                         data-testid={`cc-tl-pending-group-sel-${g.key}`} />
                  <span className="text-xs font-semibold truncate">{g.label}</span>
                </label>
                <span className="text-[10px] font-mono text-muted-foreground shrink-0">{g.tasks.length} task{g.tasks.length === 1 ? "" : "s"}</span>
              </div>
              <table className="w-full text-[11px]">
                <thead className="bg-muted/20 text-[10px] uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-semibold w-8" />
                    <th className="text-left px-3 py-1.5 font-semibold w-24">Due</th>
                    <th className="text-left px-3 py-1.5 font-semibold">Task</th>
                    <th className="text-left px-3 py-1.5 font-semibold">Deliverable</th>
                    {groupBy === "assignee"
                      ? <th className="text-left px-3 py-1.5 font-semibold w-40">Project</th>
                      : <th className="text-left px-3 py-1.5 font-semibold w-40">Assignee</th>}
                    <th className="text-left px-3 py-1.5 font-semibold w-28">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {g.tasks.map((t) => (
                    <tr key={t.id} className={`border-t border-border/40 hover:bg-muted/30 ${selected.has(t.id) ? "bg-primary/5" : ""}`}
                        data-testid={`cc-tl-pending-row-${t.id}`}>
                      <td className="px-3 py-1">
                        <input type="checkbox"
                               checked={selected.has(t.id)}
                               onChange={() => toggleSel(t.id)}
                               data-testid={`cc-tl-pending-sel-${t.id}`} />
                      </td>
                      <td className="px-3 py-1 font-mono text-[10px]">
                        {t.due_date
                          ? <span className={t.due_date < new Date().toISOString().slice(0, 10) ? "text-rose-600 font-semibold" : "text-muted-foreground"}>{t.due_date}</span>
                          : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-1">
                        <button type="button"
                                onClick={() => onOpenTask?.(t.id)}
                                className="text-left hover:text-primary hover:underline"
                                data-testid={`cc-tl-pending-task-${t.id}`}>
                          {t.title || "Task"}
                        </button>
                      </td>
                      <td className="px-3 py-1 text-muted-foreground">
                        {t._item_name ? (
                          <button type="button"
                                  onClick={() => {
                                    const it = { id: t._item_id, name: t._item_name };
                                    onOpenItem?.(it);
                                  }}
                                  className="text-left hover:text-primary hover:underline">
                            {t._item_name}
                          </button>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-1 text-muted-foreground">
                        {groupBy === "assignee"
                          ? (t._project_name || "—")
                          : (t._assignee_name || "—")}
                      </td>
                      <td className="px-3 py-1">
                        <span className="chip text-[9px] bg-muted/60 text-foreground/70 border-border px-1.5">
                          {(t.status || "open").replaceAll("_", " ")}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            );
          })}
        </div>
        <DialogFooter className="px-5 py-3 border-t border-border bg-muted/20">
          <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="cc-tl-pending-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Carry-forward dialog ----
function CarryForwardDialog({ wsId, defaultTarget, onClose, onApplied }) {
  const [sourceMonth, setSourceMonth] = useState(shiftMonth(defaultTarget, -1));
  const [targetMonth, setTargetMonth] = useState(defaultTarget);
  const [preview, setPreview] = useState(null); // { moves, source_items, target_items }
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  // R289d · Feb 2026 — Weekend / holiday conflict handling.
  const [globalShift, setGlobalShift] = useState("keep");
  const [overrides, setOverrides] = useState({});   // item_id → date

  const monthOptions = useMemo(() => {
    const arr = [];
    for (let i = -12; i <= 6; i++) arr.push(shiftMonth(defaultTarget, i));
    return arr;
  }, [defaultTarget]);

  const runPreview = async () => {
    if (sourceMonth === targetMonth) {
      toast.error("Source and target must differ");
      return;
    }
    setLoading(true);
    setPreview(null);
    setOverrides({});
    try {
      const r = await api.post(`/workspaces/${wsId}/content-calendar/carry-forward-dates`, {
        source_month: sourceMonth, target_month: targetMonth, dry_run: true,
        default_conflict_shift: globalShift,
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Preview failed"));
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!preview?.moves?.length) return;
    setApplying(true);
    try {
      const dateOverrides = {};
      Object.entries(overrides).forEach(([id, val]) => {
        if (val && val !== "global") dateOverrides[id] = val;
      });
      const r = await api.post(`/workspaces/${wsId}/content-calendar/carry-forward-dates`, {
        source_month: sourceMonth, target_month: targetMonth, dry_run: false,
        default_conflict_shift: globalShift,
        date_overrides: dateOverrides,
      });
      toast.success(`Applied ${r.data.applied} date moves${r.data.conflicts_count ? ` · ${r.data.conflicts_count} off-day conflict(s) handled` : ""}`);
      onApplied?.();
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Apply failed"));
    } finally {
      setApplying(false);
    }
  };

  const setRowOverride = (id, val) => {
    setOverrides((prev) => ({ ...prev, [id]: val }));
  };
  const conflictMoves = (preview?.moves || []).filter((m) => m.conflict);
  const cleanMoves = (preview?.moves || []).filter((m) => !m.conflict);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl rounded-none max-h-[92vh] overflow-y-auto" data-testid="cc-tl-carry-dialog">
        <DialogHeader>
          <DialogTitle>Copy dates from another month</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Source month</div>
              <Select value={sourceMonth} onValueChange={setSourceMonth}>
                <SelectTrigger className="h-8 rounded-none text-[12px]" data-testid="cc-tl-carry-source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Target month</div>
              <Select value={targetMonth} onValueChange={setTargetMonth}>
                <SelectTrigger className="h-8 rounded-none text-[12px]" data-testid="cc-tl-carry-target">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-none" onClick={runPreview} disabled={loading} data-testid="cc-tl-carry-preview-btn">
              {loading ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Loading…</> : "Preview"}
            </Button>
            {preview && (
              <span className="text-[11px] text-muted-foreground" data-testid="cc-tl-carry-preview-count">
                {preview.moves.length} of {preview.target_items} items will move
                {preview.conflicts_count > 0 && <span className="ml-2 text-amber-700 font-semibold">· ⚠ {preview.conflicts_count} on off-day</span>}
              </span>
            )}
          </div>

          {conflictMoves.length > 0 && (
            <div className="border border-amber-400 bg-amber-50 dark:bg-amber-950/30" data-testid="cc-tl-conflict-panel">
              <div className="px-3 py-2 border-b border-amber-300 flex items-center gap-3 flex-wrap">
                <strong className="text-xs uppercase tracking-widest text-amber-900 dark:text-amber-100">Off-day conflicts</strong>
                <span className="text-[11px] text-muted-foreground flex-1">Global default:</span>
                <Select value={globalShift} onValueChange={(v) => { setGlobalShift(v); runPreview(); }}>
                  <SelectTrigger className="h-8 w-52 rounded-none text-xs" data-testid="cc-tl-conflict-global"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="keep">Keep on that day</SelectItem>
                    <SelectItem value="previous_working">Previous working day</SelectItem>
                    <SelectItem value="next_working">Next working day</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-amber-200" data-testid="cc-tl-conflict-list">
                {conflictMoves.map((mv, i) => {
                  const val = overrides[mv.item_id] || "global";
                  const c = mv.conflict;
                  return (
                    <div key={mv.item_id} className="px-3 py-1.5 text-xs grid grid-cols-[110px_1fr_180px] gap-2 items-center" data-testid={`cc-tl-conflict-row-${i}`}>
                      <div>
                        <div className="font-mono">{mv.to_date}</div>
                        <div className="text-[10px] text-amber-700 uppercase tracking-wide">{c.label}</div>
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{mv.name}</div>
                        <div className="text-[10px] text-muted-foreground truncate">{mv.brand_name || ""}</div>
                      </div>
                      <Select value={val} onValueChange={(v) => setRowOverride(mv.item_id, v)}>
                        <SelectTrigger className="h-8 rounded-none text-xs" data-testid={`cc-tl-conflict-override-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="global">Use global default</SelectItem>
                          <SelectItem value={c.previous_working}>Previous working ({c.previous_working})</SelectItem>
                          <SelectItem value={c.next_working}>Next working ({c.next_working})</SelectItem>
                          <SelectItem value={mv.to_date}>Keep on {mv.to_date}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {cleanMoves.length > 0 && (
            <div className="border border-border max-h-64 overflow-auto">
              <table className="w-full text-[11px]">
                <thead className="bg-muted/40 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">Brand</th>
                    <th className="text-left px-2 py-1">Deliverable</th>
                    <th className="text-left px-2 py-1">From</th>
                    <th className="text-left px-2 py-1">To</th>
                  </tr>
                </thead>
                <tbody>
                  {cleanMoves.slice(0, 200).map((mv) => (
                    <tr key={mv.item_id} className="border-t border-border/40">
                      <td className="px-2 py-1 truncate max-w-[120px]">{mv.brand_name || "—"}</td>
                      <td className="px-2 py-1 truncate max-w-[220px]">{mv.name}</td>
                      <td className="px-2 py-1 font-mono">{mv.from_date}</td>
                      <td className="px-2 py-1 font-mono text-primary">{mv.to_date}</td>
                    </tr>
                  ))}
                  {cleanMoves.length > 200 && (
                    <tr><td colSpan={4} className="text-[10px] text-muted-foreground px-2 py-1">+ {cleanMoves.length - 200} more</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {preview && preview.moves.length === 0 && (
            <div className="text-[11px] text-muted-foreground border border-dashed border-border p-3 text-center">
              Nothing to move — target already matches source pattern or has no matching seq keys.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} data-testid="cc-tl-carry-close">Cancel</Button>
          <Button className="rounded-none" onClick={apply} disabled={!preview?.moves?.length || applying} data-testid="cc-tl-carry-apply">
            {applying ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Applying…</> : `Apply ${preview?.moves?.length || 0} moves`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Iter · Feb 2026 · R257 — Redistribute (re-spread) dates dialog.
// Preview + one-click apply of a call to
// POST /workspaces/{ws}/content-calendar/redistribute-dates. Great
// for fixing legacy months where the old generator piled every item
// on the same day (e.g., day 15 in production).
function RespreadDatesDialog({ wsId, month, onClose, onApplied }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [overrideManual, setOverrideManual] = useState(false);
  const [packages, setPackages] = useState([]);       // [{id,name,brand_name,active}]
  const [selPkgIds, setSelPkgIds] = useState([]);     // empty = all packages

  // Iter · Feb 2026 · R258 — load active packages so users can scope
  // the re-spread to specific packages instead of the whole month.
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/packages?status=active`)
      .then((r) => setPackages((r.data || []).map((p) => ({
        id: p.id, name: p.name, brand_name: p.brand_name,
      }))))
      .catch(() => setPackages([]));
  }, [wsId]);

  const runPreview = useCallback(async () => {
    setLoading(true);
    try {
      const body = { month, dry_run: true, override_manual: overrideManual };
      if (selPkgIds.length > 0) body.package_ids = selPkgIds;
      const r = await api.post(
        `/workspaces/${wsId}/content-calendar/redistribute-dates`, body,
      );
      setPreview(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Preview failed"));
    } finally {
      setLoading(false);
    }
  }, [wsId, month, overrideManual, selPkgIds]);

  useEffect(() => { runPreview(); }, [runPreview]);

  const apply = async () => {
    setApplying(true);
    try {
      const body = { month, dry_run: false, override_manual: overrideManual };
      if (selPkgIds.length > 0) body.package_ids = selPkgIds;
      const r = await api.post(
        `/workspaces/${wsId}/content-calendar/redistribute-dates`, body,
      );
      const snapId = r.data?.snapshot_id;
      toast.success(
        `Re-spread ${r.data.applied} deliverable${r.data.applied === 1 ? "" : "s"} across ${month}`,
        snapId ? {
          duration: 15000,
          action: {
            label: "Undo",
            onClick: async () => {
              try {
                const u = await api.post(
                  `/workspaces/${wsId}/content-calendar/redistribute-undo/${snapId}`,
                );
                toast.success(`Reverted ${u.data.reverted} deliverable date${u.data.reverted === 1 ? "" : "s"}`);
                onApplied?.();
              } catch (err) {
                toast.error(apiErrorMessage(err, "Undo failed"));
              }
            },
          },
        } : undefined,
      );
      onApplied?.();
      onClose?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Re-spread failed"));
    } finally {
      setApplying(false);
    }
  };

  const togglePkg = (pid) => setSelPkgIds((prev) =>
    prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]);

  // Mini timeline visual — bar chart of items per day (from vs to).
  const days = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    return new Date(y, m, 0).getDate();
  }, [month]);
  const distribution = useMemo(() => {
    if (!preview) return null;
    const from = Array(days).fill(0);
    const to = Array(days).fill(0);
    for (const mv of preview.moves || []) {
      if (mv.from_date) {
        const d = parseInt(mv.from_date.slice(8), 10);
        if (d >= 1 && d <= days) from[d - 1] += 1;
      }
      const td = parseInt((mv.to_date || "").slice(8), 10);
      if (td >= 1 && td <= days) to[td - 1] += 1;
    }
    const maxFrom = Math.max(1, ...from);
    const maxTo = Math.max(1, ...to);
    const maxAll = Math.max(maxFrom, maxTo);
    return { from, to, max: maxAll };
  }, [preview, days]);

  const brandPkgs = useMemo(() => {
    const byBrand = new Map();
    for (const p of packages) {
      const b = p.brand_name || "—";
      if (!byBrand.has(b)) byBrand.set(b, []);
      byBrand.get(b).push(p);
    }
    return [...byBrand.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [packages]);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-auto" data-testid="cc-tl-respread-dialog">
        <DialogHeader>
          <DialogTitle>Re-spread deliverable dates · {month}</DialogTitle>
        </DialogHeader>

        <div className="text-[12px] text-muted-foreground">
          Redistributes deliverables in <b>{month}</b> evenly across the calendar,
          grouped by package × line item. Sundays are skipped when possible.
          Linked open tasks' due dates follow the deliverable.
        </div>

        {/* Manual-override toggle */}
        <label className="flex items-start gap-2 text-[12px] cursor-pointer border border-border p-2 bg-muted/20"
               data-testid="cc-tl-respread-override-wrap">
          <input type="checkbox" checked={overrideManual}
                 onChange={(e) => setOverrideManual(e.target.checked)}
                 className="mt-0.5"
                 data-testid="cc-tl-respread-override" />
          <div className="flex-1">
            <div className="font-semibold">
              Also override dates I manually changed
            </div>
            <div className="text-[11px] text-muted-foreground">
              {overrideManual
                ? "⚠ Every deliverable will be re-spread — including any dates you or a teammate set manually."
                : "Deliverables you rescheduled by hand will be preserved. Only auto-generated dates get re-spread."}
            </div>
          </div>
        </label>

        {/* Package scope */}
        {packages.length > 1 && (
          <div className="border border-border p-2">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Packages</span>
              <button type="button"
                      className="text-[10px] text-primary hover:underline"
                      onClick={() => setSelPkgIds([])}
                      data-testid="cc-tl-respread-selectall">
                All packages ({packages.length})
              </button>
              {selPkgIds.length > 0 && (
                <span className="text-[10px] text-muted-foreground" data-testid="cc-tl-respread-selcount">
                  {selPkgIds.length} selected
                </span>
              )}
            </div>
            <div className="max-h-32 overflow-auto space-y-1">
              {brandPkgs.map(([brand, pkgs]) => (
                <div key={brand}>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{brand}</div>
                  <div className="flex flex-wrap gap-1 pl-2">
                    {pkgs.map((p) => {
                      const sel = selPkgIds.includes(p.id);
                      return (
                        <label key={p.id}
                               className={`text-[11px] px-1.5 py-0.5 border cursor-pointer ${
                                 sel
                                   ? "border-primary bg-primary/10 text-primary font-semibold"
                                   : "border-border text-muted-foreground hover:border-primary"}`}
                               data-testid={`cc-tl-respread-pkgchip-${p.id}`}>
                          <input type="checkbox" checked={sel}
                                 onChange={() => togglePkg(p.id)}
                                 className="mr-1 h-2.5 w-2.5" />
                          {p.name}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-sm py-4 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Analysing current dates…</div>
        ) : !preview ? null : (
          <>
            {/* Mini timeline: before vs after per day */}
            {distribution && preview.moves.length > 0 && (
              <div className="border border-border p-2" data-testid="cc-tl-respread-mini-timeline">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
                  Distribution preview · <span className="text-rose-600">before</span> vs <span className="text-emerald-600">after</span>
                </div>
                <div className="grid" style={{ gridTemplateColumns: `36px repeat(${days}, minmax(12px, 1fr))` }}>
                  <div className="text-[9px] text-rose-600 text-right pr-1 font-mono self-end">Was</div>
                  {distribution.from.map((n, i) => (
                    <div key={`f-${i}`} className="flex items-end justify-center h-8 border-r border-border/40">
                      {n > 0 && (
                        <div className="w-full bg-rose-500/80" style={{ height: `${(n / distribution.max) * 100}%` }}
                             title={`Day ${i + 1}: ${n} item${n === 1 ? "" : "s"} before`} />
                      )}
                    </div>
                  ))}
                  <div className="text-[9px] text-emerald-700 text-right pr-1 font-mono self-end">Will be</div>
                  {distribution.to.map((n, i) => (
                    <div key={`t-${i}`} className="flex items-end justify-center h-8 border-r border-border/40">
                      {n > 0 && (
                        <div className="w-full bg-emerald-500/80" style={{ height: `${(n / distribution.max) * 100}%` }}
                             title={`Day ${i + 1}: ${n} item${n === 1 ? "" : "s"} after`} />
                      )}
                    </div>
                  ))}
                  <div />
                  {Array.from({ length: days }, (_, i) => (
                    <div key={`d-${i}`} className="text-[8px] text-muted-foreground text-center font-mono">{i + 1}</div>
                  ))}
                </div>
              </div>
            )}

            {preview.moves.length === 0 ? (
              <div className="border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-[12px]" data-testid="cc-tl-respread-nochange">
                {preview.skipped_manual > 0
                  ? `No moves proposed — ${preview.skipped_manual} deliverable(s) with manual dates were preserved. Tick the override toggle above to include them.`
                  : "Every deliverable is already evenly distributed — no changes needed."}
              </div>
            ) : (
              <div className="border border-border max-h-56 overflow-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left">Deliverable</th>
                      <th className="px-2 py-1 text-left">Package</th>
                      <th className="px-2 py-1 text-right">From</th>
                      <th className="px-2 py-1 text-right">→ To</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {preview.moves.map((mv) => (
                      <tr key={mv.item_id} data-testid={`cc-tl-respread-row-${mv.item_id}`}
                          className={mv.manually_rescheduled ? "bg-amber-50 dark:bg-amber-500/10" : ""}>
                        <td className="px-2 py-1 truncate max-w-[240px]">
                          {mv.manually_rescheduled && (
                            <span className="chip text-[8px] bg-amber-500/20 text-amber-800 border-amber-500/40 mr-1 px-1 py-0"
                                  title="You previously set this date manually">manual</span>
                          )}
                          {mv.name}
                        </td>
                        <td className="px-2 py-1 text-muted-foreground truncate max-w-[140px]">{mv.package_name || mv.brand_name || "—"}</td>
                        <td className="px-2 py-1 text-right font-mono text-muted-foreground">{mv.from_date || "—"}</td>
                        <td className="px-2 py-1 text-right font-mono font-semibold text-primary">{mv.to_date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {preview.skipped_manual > 0 && preview.moves.length > 0 && (
              <div className="text-[11px] text-amber-800 bg-amber-50 dark:bg-amber-500/10 border border-amber-500/30 px-2 py-1"
                   data-testid="cc-tl-respread-skipped-note">
                {preview.skipped_manual} deliverable{preview.skipped_manual === 1 ? "" : "s"} with manual dates preserved. Tick "Override manual dates" to include them.
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} data-testid="cc-tl-respread-cancel">Cancel</Button>
          <Button className="rounded-none" onClick={apply}
                  disabled={!preview?.moves?.length || applying}
                  data-testid="cc-tl-respread-apply">
            {applying
              ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Applying…</>
              : `Apply ${preview?.moves?.length || 0} moves`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

